import { onMounted, ref, watch } from 'vue';
import moment from 'moment';
import { useI18n } from '@/composables';

type TimePart = {
  hour: number;
  minute: number;
};

type FlowTimeOfDayConditionEntry = {
  predicate: string;
  values: {
    from: TimePart;
    to: TimePart;
  };
};

type FlowConditions = Record<string, FlowTimeOfDayConditionEntry>;

type TimeType = 'from' | 'to';

const timeRegex = /^(\d{1,2}):(\d{1,2})$/;

const getDefaultTimeOfDay = (): FlowTimeOfDayConditionEntry => ({
  predicate: 'EQUALS',
  values: {
    from: { hour: 0, minute: 0 },
    to: { hour: 0, minute: 0 },
  },
});

export const useFlowTimeOfDayCondition = (conditions: FlowConditions) => {
  const { t } = useI18n();

  const predicate = ref('EQUALS');
  const from = ref('');
  const to = ref('');

  const handleTimeChanged = (type: TimeType, value: string) => {
    const match = value.match(timeRegex);
    if (!match) return;

    let hours = +match[1];
    if (hours > 23) {
      hours = 23;
    } else if (hours < 0) {
      hours = 0;
    }

    let minutes = +match[2];
    if (minutes > 59) {
      minutes = 59;
    } else if (minutes < 0) {
      minutes = 0;
    }

    conditions.timeOfDay.values[type].hour = hours;
    conditions.timeOfDay.values[type].minute = minutes;
  };

  const resetTime = (type: TimeType) => {
    const subject = conditions.timeOfDay.values[type];
    const value = moment(`${subject.hour}:${subject.minute}`, 'HH:mm').format('HH:mm');

    if (type === 'from') {
      from.value = value;
    } else {
      to.value = value;
    }
  };

  watch(to, (value) => {
    handleTimeChanged('to', value);
  });

  watch(from, (value) => {
    handleTimeChanged('from', value);
  });

  watch(predicate, (value) => {
    conditions.timeOfDay.predicate = value;
  });

  onMounted(() => {
    if (!conditions.timeOfDay) {
      conditions.timeOfDay = getDefaultTimeOfDay();
    }

    predicate.value = conditions.timeOfDay.predicate || predicate.value;
    resetTime('to');
    resetTime('from');
  });

  return {
    t,
    predicate,
    from,
    to,
  };
};
